import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { ElementListItem, SortableElementListItem } from "../ElementListItem";
import { useCallback, useMemo, useRef, useState } from "react";
import { getElementsByScene } from "@repo/api/utils/composition";
import { IconUpload } from "@tabler/icons-react";
import { SceneTypeDto } from "@repo/api/overlays";
import { getCompositionTimings } from "@repo/video-engine-i2v/utils/timing";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { cn } from "@/lib/utils";

type Props = {
  sceneType: SceneTypeDto;
};

export default function ElementsList({ sceneType }: Props) {
  const updateOverlaysTabState = useEditorStore(
    (state) => state.service.updateOverlaysTabState,
  );

  const handleElementDrag = useEditorStore(
    (state) => state.service.handleElementDrag,
  );

  const seekToElement = useEditorStore((state) => state.service.seekToElement);

  const setSelectedElementIds = useEditorStore(
    (state) => state.service.setSelectedElementIds,
  );

  const composition = useEditorStore((state) => state.video?.compositionJson);
  const animatedImages = useEditorStore((state) => state.animatedImages);

  const elements = useMemo(
    () => getElementsByScene(composition, sceneType),
    [composition, sceneType],
  );
  const selectedElementIds = useEditorStore(
    (state) => state.selectedElementIds,
  );

  // Anchor for Shift-click range selection.
  const lastClickedIdRef = useRef<string | null>(null);

  const handleElementClick = useCallback(
    (elementExternalId: string, event: React.MouseEvent) => {
      const elementIds = elements.map((element) => element.externalId);

      if (event.shiftKey) {
        // A stale anchor (selection cleared elsewhere) must not seed a range.
        const anchorIndex =
          selectedElementIds.length > 0 && lastClickedIdRef.current
            ? elementIds.indexOf(lastClickedIdRef.current)
            : -1;
        const targetIndex = elementIds.indexOf(elementExternalId);

        if (anchorIndex === -1) {
          lastClickedIdRef.current = elementExternalId;
          setSelectedElementIds([elementExternalId]);
          return;
        }

        setSelectedElementIds(
          elementIds.slice(
            Math.min(anchorIndex, targetIndex),
            Math.max(anchorIndex, targetIndex) + 1,
          ),
        );
        return;
      }

      if (event.metaKey || event.ctrlKey) {
        if (selectedElementIds.includes(elementExternalId)) {
          setSelectedElementIds(
            selectedElementIds.filter((id) => id !== elementExternalId),
          );

          if (lastClickedIdRef.current === elementExternalId) {
            lastClickedIdRef.current = null;
          }
        } else {
          setSelectedElementIds([...selectedElementIds, elementExternalId]);
          lastClickedIdRef.current = elementExternalId;
        }
        return;
      }

      lastClickedIdRef.current = elementExternalId;
      setSelectedElementIds([elementExternalId]);
      updateOverlaysTabState((state) => {
        state.currentElement = {
          externalId: elementExternalId,
          sceneType: sceneType,
        };
      });

      seekToElement(elementExternalId, sceneType);
    },
    [
      elements,
      selectedElementIds,
      updateOverlaysTabState,
      setSelectedElementIds,
      seekToElement,
      sceneType,
    ],
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const handleImageDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      setActiveId(active.id as string);
    },
    [setActiveId],
  );

  const handleImageDragEnd = useCallback(
    (event: DragEndEvent) => {
      handleElementDrag(event, sceneType);
      setActiveId(null);
      lastClickedIdRef.current = null;
    },
    [handleElementDrag, setActiveId, sceneType],
  );

  const compositionTimings = useMemo(() => {
    if (!composition) return null;

    return getCompositionTimings(composition, animatedImages);
  }, [composition, animatedImages]);

  if (elements.length === 0 && sceneType === "mainScene") {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center text-sm text-text-secondary">
        <p className="mt-4 w-90 text-center">
          Add elements like logos, property details, or agent contact
          information to your video.
        </p>
      </div>
    );
  }

  if (elements.length === 0 && sceneType !== "mainScene") {
    return (
      <p className="mt-2 px-6 text-sm text-text-secondary">
        Add elements from the <span className="font-semibold">bottom bar</span>{" "}
        to get started.
      </p>
    );
  }

  return (
    <div
      data-keep-selection
      className={cn(
        "flex flex-col gap-1",
        sceneType === "mainScene" ? "px-4.5 py-3" : "px-4",
      )}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleImageDragStart}
        onDragEnd={handleImageDragEnd}
      >
        <SortableContext
          items={elements.map((element) => element.externalId)}
          strategy={verticalListSortingStrategy}
        >
          {elements.map((element) => (
            <SortableElementListItem
              key={element.externalId}
              elementExternalId={element.externalId}
              onClick={handleElementClick}
              sceneType={sceneType}
              compositionTimings={compositionTimings}
            />
          ))}
        </SortableContext>
        <DragOverlay>
          {activeId ? (
            <ElementListItem
              key={activeId}
              elementExternalId={activeId}
              onClick={() => {}}
              sceneType={sceneType}
              compositionTimings={compositionTimings}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

export function MediaUploadDragHover() {
  return (
    <div className="absolute inset-0 m-2 flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-primary-400 bg-primary-100/95">
      <IconUpload stroke={1.5} size={32} color="url(#icon-gradient)" />
      <div className="h-4"></div>
      <h1 className="text-base font-semibold">Drop file here</h1>
      <p className="text-sm text-text-secondary">to add it to the video</p>
    </div>
  );
}

export function MediaUploadingIndicator() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white/95">
      <LoadingSpinner size={20} />
      <div className="w-2"></div>
      <p className="text-sm font-medium">Uploading media...</p>
    </div>
  );
}
